import React, { useState, useEffect, useRef } from 'react';
import ProposedPlan from './ProposedPlan';
import ChatMessageMarkdown from './ChatMessageMarkdown';
import { LOOKUP_TOOL_DESCRIPTIONS } from './lookupToolDescriptions';
import ResearchMissionPanel from '../ResearchMissionPanel';
import DeepResearchProgress from './DeepResearchProgress';
import type { ResearchMission, ResearchProgressState, HandoffPackage } from '../../types';
import { useProjectStore } from '../../stores/projectStore';
import {
    deleteWorkspaceUpload,
    submitMessageFeedback,
    uploadWorkspaceFile,
    type UploadedWorkspaceFile,
} from '../../apiService';
import FeedbackModal, { type FeedbackSentiment } from './FeedbackModal';
import MessageFeedbackControls from './MessageFeedbackControls';
import MessageSourceIcons from './MessageSourceIcons';
import { Paperclip, X, FileText, Bot } from 'lucide-react';
import { useFollowLatest } from '../../hooks/useFollowLatest';
import {
    type ChatUploadConfig,
    DEFAULT_CHAT_UPLOAD_CONFIG,
    CHAT_UPLOAD_ACCEPT,
    formatFileSize,
    isSupportedUploadFile,
    loadChatUploadConfig,
} from './chatUploadConfig';

/**
 * Chat-message link-click dispatchers. Thin wrappers over the
 * `window.handle*Click` globals that App.tsx registers (see
 * App.tsx:2432,2440,2547). They let ChatMessageMarkdown dispatch
 * citation and handoff link clicks without knowing about App-level
 * architecture. Report clicks use an explicit React prop because they
 * fetch data and mutate App-owned chat state. Declared at module scope so every
 * ChatMessageMarkdown render site (main timeline, UserMessage,
 * InlineActivityTimeline) can pass the same stable references — no
 * prop drilling, no re-renders from fresh closures.
 *
 * The handoff global takes no argument by design (it opens the most
 * recently stored handoff); the chip passes its id, which is
 * intentionally ignored here.
 */
const dispatchCitationClick = (markerId: string) => {
    const fn = (window as any).handleReferenceClick;
    if (typeof fn === 'function') {
        fn(markerId);
    } else {
        console.warn(
            '[chat] citation chip clicked but window.handleReferenceClick is not registered',
            { markerId },
        );
    }
};
const dispatchHandoffClick = (_handoffId: string) => {
    const fn = (window as any).handleHandoffClick;
    if (typeof fn === 'function') {
        fn();
    } else {
        console.warn(
            '[chat] handoff chip clicked but window.handleHandoffClick is not registered',
        );
    }
};
// Activity log event type for timeline
interface StreamingActivityEvent {
    type: string;
    content: string;
    timestamp: number;
}

export interface SendMessageOptions {
    uploadedFiles?: UploadedWorkspaceFile[];
    displayMessage?: string;
    clientMessageId?: string;
    /** BAR-192: this send is a Fix / Find-source repair dispatch. Rides into the
     *  backend `context.repair_dispatch` so the done-gate enforces real edits +
     *  verify before the turn can end — independent of the instruction wording. */
    repair?: boolean;
}

interface GeminiChatAreaProps {
    history: any[];
    onSendMessage: (
        message: string,
        researchDepth: string,
        useMultiStage: boolean,
        options?: SendMessageOptions,
    ) => void;
    isLoading: boolean;
    streamingStatus?: string | null;  // Real-time progress messages from WebSocket
    streamingResponse?: string;  // Accumulated streaming tokens from orchestrator
    streamingThinking?: string;  // Accumulated thinking tokens from orchestrator
    streamingActivityLog?: StreamingActivityEvent[];  // Timeline of actions during streaming
    activeToolCall?: string | null;  // Currently executing tool
    onCancelWorkflow?: () => void;  // Cancel current workflow
    activeChat: any;
    compact: boolean;
    onServiceSelect: (serviceType: string) => void;
    currentReport: any;
    onCloseReport: () => void;
    setActiveView: (view: string) => void;
    userData: any;
    isCanvasMode: boolean;
    setIsCanvasMode: (mode: boolean) => void;
    researchMission?: ResearchMission | null;  // Research mission (question, plan, progress)
    hasReconnectedSession?: boolean;  // True when reconnected to an active agent session
    tokenUsage?: {inputTokens: number; outputTokens: number; totalTokens: number};  // Actual API token usage
    // Deep research progress
    researchProgress?: ResearchProgressState | null;
    getResearchProgress?: (key: string) => ResearchProgressState | undefined;
    activeResearchTrackingId?: string | null;  // Current research's tracking_id for fallback lookup
    // Handoff package
    handoffPackage?: HandoffPackage | null;
    onOpenHandoffPanel?: () => void;
    // Pending question from ask_user_question tool
    pendingQuestion?: {question: string; context?: string; options?: string[]; question_type?: string} | null;
    onAnswerQuestion?: (answer: string) => void;
    onReportClick?: (reportId: string) => void;
    // Pre-fill the chat input (e.g., from Home agent action)
    initialInput?: string;
    // When true, the component will programmatically send `initialInput`
    // once on mount instead of only pre-filling it. Used by the new-project
    // flow so the first message the user typed becomes the first chat turn
    // inside the freshly-created workspace.
    autoSendOnMount?: boolean;
}

// Activity log event type
interface ActivityEvent {
    type: string;
    content: string;
    timestamp: number;
    // BAR-1270 — server-stamped authorship, carried through by
    // projectStore.getDisplayHistory. Undefined on every event the server did
    // not stamp, which is the legacy case and renders exactly as before.
    author?: 'user' | 'bark' | 'external_agent';
    onBehalfOf?: string;
}

interface FeedbackTarget {
    messageId: string;
    targetSnippet: string;
}

// Upload config + validation moved to ./chatUploadConfig so the new-project
// seed composer shares one source of truth with this in-project composer.

function getFeedbackTargetText(msg: any): string {
    const activityLog = Array.isArray(msg?.activityLog) ? msg.activityLog : [];
    for (let i = activityLog.length - 1; i >= 0; i--) {
        if (activityLog[i]?.type === 'response' && activityLog[i]?.content) {
            return activityLog[i].content;
        }
    }
    return msg?.message || '';
}

function makeFeedbackSnippet(text: string): string {
    return (text || '').replace(/\s+/g, ' ').trim().slice(0, 500);
}

// Workspace Indicator — shows which workspace BARK is currently working in
function WorkspaceIndicator() {
    const barkWorkspaceName = useProjectStore(s => s.barkWorkspaceName);
    const barkWorkspace = useProjectStore(s => s.barkWorkspace);

    const TYPE_LABELS: Record<string, string> = {
        plan: 'Plan',
        research: 'Research',
        production: 'Build',
        institution_context: 'Context',
    };

    const displayLabel = barkWorkspaceName || TYPE_LABELS[barkWorkspace] || 'Plan';

    return (
        <div className="flex items-center gap-1.5 text-xs text-fg-tertiary">
            <img
                src="/Data/images/BARK_tree_2_navy.png"
                alt=""
                className="w-4 h-4 object-contain"
            />
            <span className="font-medium">{displayLabel}</span>
        </div>
    );
}

// Context Window Usage Bar - Shows current context window consumption
// `tokenUsage.inputTokens` is the FULL prompt size: billed input plus any
// cached-read and cache-write tokens, summed in App.tsx's `token_usage` case.
// It is deliberately not the API's raw `input_tokens` — with prompt caching on,
// that field carries only the uncached remainder and under-reports the bar.
function ContextWindowBar({
    tokenUsage,
    maxTokens = 200000, // Opus 4.5 context window
}: {
    tokenUsage?: {inputTokens: number; outputTokens: number; totalTokens: number};
    maxTokens?: number;
}) {
    // inputTokens is the context window usage (prompt size sent to API)
    const contextUsed = tokenUsage?.inputTokens || 0;

    // Calculate percentage of context window used
    const usagePercent = Math.min((contextUsed / maxTokens) * 100, 100);
    const usageK = Math.round(contextUsed / 1000);
    const maxK = Math.round(maxTokens / 1000);

    // Color based on usage level
    let barColor = 'bg-success';
    let textColor = 'text-success';
    if (usagePercent > 75) {
        barColor = 'bg-danger';
        textColor = 'text-danger';
    } else if (usagePercent > 50) {
        barColor = 'bg-warning';
        textColor = 'text-warning';
    }

    return (
        <div className="flex items-center gap-2 text-xs">
            <span className="text-fg-tertiary">Context:</span>
            <div className="flex-1 h-1 bg-bg-warm rounded-full overflow-hidden max-w-20">
                <div
                    className={`h-full ${barColor} transition-all duration-300`}
                    style={{ width: `${usagePercent}%` }}
                />
            </div>
            <span className={`font-medium ${textColor}`}>
                {usageK}k/{maxK}k
            </span>
        </div>
    );
}

// Tool descriptions for better UX
const TOOL_DESCRIPTIONS: {[key: string]: string} = {
    ...LOOKUP_TOOL_DESCRIPTIONS,
    'search_documents': 'Searching regulatory documents',
    'search_internal_rag': 'Searching internal knowledge base',
    'search_external_rag': 'Searching external documents',
    'search_regulatory_graph': 'Querying regulatory graph database',
    'get_document_chunk': 'Retrieving document section',
    'summarize_results': 'Summarizing findings',
    'save_to_workspace': 'Saving to workspace',
    'read_workspace': 'Reading workspace',
    'update_workspace': 'Updating workspace',
    'list_workspace_compartments': 'Listing workspace compartments',
    'create_compartment': 'Creating workspace compartment',
    'research_workflow': 'Running research workflow',
    'export_to_docs': 'Exporting to Google Docs',
    'set_research_query': 'Setting research query',
    'add_finding': 'Adding research finding',
};

function getToolDescription(toolName: string): string {
    return TOOL_DESCRIPTIONS[toolName] || `Using ${toolName}`;
}

// Component to render inline activity timeline (always visible, chronological)
// UNIFIED TIMELINE: Shows thinking + tool calls + response - ALL in one stream
// Now supports streaming mode with live content appended to timeline
function InlineActivityTimeline({
    thinking,
    activityLog,
    message,
    // Streaming props - for live content during agent execution
    streamingThinking,
    streamingResponse,
    activeToolCall,
    isStreaming = false,
    streamingStatus,
    researchProgress,
    handoffPackage,
    onOpenHandoffPanel,
    onReportClick,
}: {
    thinking?: string;
    activityLog?: ActivityEvent[];
    message?: string;  // Fallback message content if no response event in activityLog
    // Streaming props
    streamingThinking?: string;
    streamingResponse?: string;
    activeToolCall?: string | null;
    isStreaming?: boolean;
    streamingStatus?: string | null;
    researchProgress?: ResearchProgressState | null;
    handoffPackage?: HandoffPackage | null;
    onOpenHandoffPanel?: () => void;
    onReportClick?: (reportId: string) => void;
}) {
    const [isThinkingExpanded, setIsThinkingExpanded] = useState(false);
    const [isToolsExpanded, setIsToolsExpanded] = useState(false);

    // Support both old format (separate thinking) and new format (thinking_blocks in activityLog)
    const hasActivity = activityLog && activityLog.length > 0;
    const hasLegacyThinking = thinking && thinking.trim().length > 0;
    const hasStreamingContent = isStreaming && (
        (streamingThinking && streamingThinking.trim()) ||
        (streamingResponse && streamingResponse.trim()) ||
        activeToolCall
    );

    // Check if activityLog has thinking_blocks (new format)
    const hasThinkingBlocks = activityLog?.some(e => e.type === 'thinking_block');

    // Only return null if there's nothing to show (no activity, no legacy thinking, AND no streaming)
    if (!hasLegacyThinking && !hasActivity && !hasStreamingContent && !isStreaming) return null;

    // Show thinking, tool calls, and response in chronological order
    // Include tool_result so it's sorted chronologically (tool_call + tool_result grouped together, only tool_call displayed)
    // Note: 'thinking' is legacy format from older persistence, 'thinking_block' is new format
    const SHOWN_TYPES = ['thinking', 'thinking_block', 'thinking_stream', 'tool_call', 'tool_result', 'response', 'response_stream', 'research_progress', 'workspace_switch', 'user_question'];

    // Filter and sort by timestamp
    const sortedActivity = hasActivity
        ? [...activityLog!]
            .filter(e => SHOWN_TYPES.includes(e.type))
            .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))
        : [];

    // If using legacy format (separate thinking, no thinking_blocks), add thinking as first item
    let allItems: Array<{ type: string; content: string; timestamp: number }> = [];
    if (hasLegacyThinking && !hasThinkingBlocks) {
        allItems.push({
            type: 'thinking_block',
            content: thinking!,  // Full thinking content, no truncation
            timestamp: 0  // First in timeline
        });
    }
    // Add sorted activity items
    allItems = [...allItems, ...sortedActivity];

    // Don't return null here - streaming content is added after grouping
    // The early return at the top handles the case when there's truly nothing to show

    // Group consecutive items for collapsible sections
    type GroupedItem =
        | { kind: 'thinking'; items: ActivityEvent[]; isStream: boolean }
        | { kind: 'tools'; items: ActivityEvent[]; isStream?: boolean }
        | { kind: 'response'; item: ActivityEvent; isStream: boolean }
        | { kind: 'loading'; message: string }
        | { kind: 'research_progress'; savedData?: string }
        | { kind: 'workspace_switch'; data: { from: string; to: string; targetNodeName?: string; initiator: string } }
        | { kind: 'handoff_package' }
        | { kind: 'user_question'; data: { question: string; context?: string; options?: string[]; question_type?: string } };

    const groupedItems: GroupedItem[] = [];
    let i = 0;
    while (i < allItems.length) {
        const item = allItems[i];

        if (item.type === 'thinking' || item.type === 'thinking_block' || item.type === 'thinking_stream') {
            // Collect consecutive thinking items
            const thinkingItems: ActivityEvent[] = [];
            while (i < allItems.length &&
                   (allItems[i].type === 'thinking' || allItems[i].type === 'thinking_block' || allItems[i].type === 'thinking_stream')) {
                thinkingItems.push(allItems[i]);
                i++;
            }
            const hasStream = thinkingItems.some(t => t.type === 'thinking_stream');
            groupedItems.push({ kind: 'thinking', items: thinkingItems, isStream: hasStream });
        } else if (item.type === 'tool_call' || item.type === 'tool_result') {
            // Collect consecutive tool calls and results (group all tool activity together)
            const toolItems: ActivityEvent[] = [];
            while (i < allItems.length && (allItems[i].type === 'tool_call' || allItems[i].type === 'tool_result')) {
                // Only add tool_call items to display (skip tool_result for display but keep iterating)
                if (allItems[i].type === 'tool_call') {
                    toolItems.push(allItems[i]);
                }
                i++;
            }
            if (toolItems.length > 0) {
                groupedItems.push({ kind: 'tools', items: toolItems });
                // If this tool group contains research_workflow, attach progress inline
                const hasResearchTool = toolItems.some(t =>
                    t.content?.includes('research_workflow') || t.content?.includes('research workflow')
                );
                if (hasResearchTool) {
                    groupedItems.push({ kind: 'research_progress' });
                }
            }
        } else if (item.type === 'research_progress') {
            // Skip anchored/persisted research_progress events in the activity log —
            // progress is now rendered inline after the research_workflow tool_call above
            i++;
        } else if (item.type === 'workspace_switch') {
            // Workspace mode switch event — render as a banner in the timeline
            try {
                const data = JSON.parse(item.content);
                groupedItems.push({ kind: 'workspace_switch', data });
            } catch {
                // Skip malformed workspace_switch events
            }
            i++;
        } else if (item.type === 'user_question') {
            // Agent is asking the user a structured question
            try {
                const data = JSON.parse(item.content);
                console.log('[RENDER] user_question grouped:', data.question?.substring(0, 50));
                groupedItems.push({ kind: 'user_question', data });
            } catch (e) {
                console.error('[RENDER] user_question parse error:', e, item.content?.substring(0, 100));
            }
            i++;
        } else if (item.type === 'response' || item.type === 'response_stream') {
            groupedItems.push({
                kind: 'response',
                item: item,
                isStream: item.type === 'response_stream'
            });
            i++;
        } else {
            i++; // Skip unknown types
        }
    }

    // Fallback: If no response event in activityLog but we have message content, add it
    const hasResponseEvent = groupedItems.some(g => g.kind === 'response');
    if (!hasResponseEvent && message && message.trim()) {
        groupedItems.push({
            kind: 'response',
            item: { type: 'response', content: message, timestamp: Date.now() },
            isStream: false
        });
    }

    // BACKGROUND RESEARCH: research_progress is now anchored in the activity log
    // at the position of the research_workflow tool call. No need to append at bottom.

    // Deep research progress fallback — ONLY on the currently-streaming message,
    // and ONLY when research is genuinely active right now. Previous behaviour
    // also rendered the card whenever `phases.length > 0`, which caused a stale
    // Deep Research card to land at the bottom of every past turn that never
    // used research. It must stay bound to the live turn only.
    if (isStreaming && researchProgress && researchProgress.isActive) {
        const hasInlineProgress = groupedItems.some(g => g.kind === 'research_progress');
        if (!hasInlineProgress) {
            groupedItems.push({ kind: 'research_progress' });
        }
    }

    // STREAMING MODE: Append live content to the timeline
    if (isStreaming) {
        // Add streaming thinking (if any and not already in activityLog as finalized)
        if (streamingThinking && streamingThinking.trim()) {
            groupedItems.push({
                kind: 'thinking',
                items: [{ type: 'thinking_stream', content: streamingThinking, timestamp: Date.now() }],
                isStream: true
            });
        }

        // Add active tool call indicator
        if (activeToolCall) {
            groupedItems.push({
                kind: 'tools',
                items: [{ type: 'tool_call', content: activeToolCall, timestamp: Date.now() }],
                isStream: true  // Will show pulsing indicator
            });
        }

        // Add streaming response (if any)
        if (streamingResponse && streamingResponse.trim()) {
            groupedItems.push({
                kind: 'response',
                item: { type: 'response_stream', content: streamingResponse, timestamp: Date.now() },
                isStream: true
            });
        }

        // Show loading indicator if nothing is streaming yet
        if (!streamingThinking?.trim() && !streamingResponse?.trim() && !activeToolCall) {
            groupedItems.push({
                kind: 'loading',
                message: streamingStatus || 'Processing...'
            } as any);
        }
    }

    return (
        <div className="mt-1">
            {/* UNIFIED TIMELINE - thin line through markers */}
            <div className="relative">
                {/* Vertical line - very thin, positioned to go through center of markers */}
                <div className="absolute left-[9px] top-3 bottom-3 w-px bg-bg-warm" />

                {/* Timeline items */}
                <div className="space-y-2">
                    {groupedItems.map((group, groupIndex) => {
                        // THINKING SECTION - Collapsible
                        if (group.kind === 'thinking') {
                            const allThinkingContent = group.items.map(t => t.content).join('\n\n');
                            const lineCount = allThinkingContent.split('\n').filter(l => l.trim()).length;

                            return (
                                <div key={`thinking-group-${groupIndex}`} className="relative flex items-start">
                                    {/* Thinking marker — cyan (node-reasoning) per §6.5 */}
                                    <div className={`relative z-10 w-[10px] h-[10px] rounded-full bg-node-reasoning border border-surface shadow-bark-xs flex-shrink-0 mt-0.5 ml-1 ${group.isStream ? 'animate-pulse' : ''}`} />

                                    <div className="flex-1 ml-3">
                                        <button
                                            onClick={() => setIsThinkingExpanded(!isThinkingExpanded)}
                                            className="flex items-center gap-2 text-xs text-fg-tertiary hover:text-fg-secondary transition-colors py-0.5"
                                        >
                                            <svg className={`w-3 h-3 transition-transform ${isThinkingExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                            <span className="font-medium text-fg-secondary">Thinking</span>
                                            <span className="text-fg-tertiary">({lineCount} lines)</span>
                                            {group.isStream && <span className="inline-block w-1.5 h-3 bg-fg-tertiary animate-pulse ml-1" />}
                                        </button>

                                        {isThinkingExpanded && (
                                            <div className="mt-2 ml-5 pl-3 border-l border-border-default bg-bg-warm rounded-r py-2 pr-3 max-h-64 overflow-y-auto">
                                                <div className="text-xs text-fg-tertiary whitespace-pre-wrap font-mono leading-relaxed">
                                                    {allThinkingContent}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        }

                        // TOOL CALLS SECTION - Collapsible when multiple
                        if (group.kind === 'tools') {
                            const toolCount = group.items.length;

                            // Single tool - show inline (with pulsing if streaming)
                            if (toolCount === 1) {
                                const toolName = (group.items[0].content || '').replace(/^🔧\s*/, '').split('(')[0].trim();
                                return (
                                    <div key={`tool-single-${groupIndex}`} className="relative flex items-start">
                                        {/* Tool marker — teal accent when active, fg-secondary when idle */}
                                        <div className={`relative z-10 w-[10px] h-[10px] rounded-full border border-surface shadow-bark-xs flex-shrink-0 mt-0.5 ml-1 ${group.isStream ? 'bg-secondary animate-pulse' : 'bg-fg-secondary'}`} />

                                        <div className="flex items-center gap-2 ml-3 py-0.5">
                                            <span className="text-xs text-fg-secondary">
                                                {group.isStream ? `Using ${toolName}...` : getToolDescription(toolName)}
                                            </span>
                                        </div>
                                    </div>
                                );
                            }

                            // Multiple tools - collapsible
                            return (
                                <div key={`tools-group-${groupIndex}`} className="relative flex items-start">
                                    {/* Tools-group marker — neutral fg-secondary (collapsed batch) */}
                                    <div className="relative z-10 w-[10px] h-[10px] rounded-full bg-fg-secondary border border-surface shadow-bark-xs flex-shrink-0 mt-0.5 ml-1" />

                                    <div className="flex-1 ml-3">
                                        <button
                                            onClick={() => setIsToolsExpanded(!isToolsExpanded)}
                                            className="flex items-center gap-2 text-xs text-fg-secondary hover:text-fg transition-colors py-0.5"
                                        >
                                            <svg className={`w-3 h-3 transition-transform ${isToolsExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                            <span className="font-medium">Tool calls</span>
                                            <span className="text-fg-tertiary">({toolCount})</span>
                                        </button>

                                        {isToolsExpanded && (
                                            <div className="mt-1 ml-5 space-y-1">
                                                {group.items.map((tool, toolIndex) => {
                                                    const toolName = (tool.content || '').replace(/^🔧\s*/, '').split('(')[0].trim();
                                                    return (
                                                        <div key={toolIndex} className="flex items-center gap-2 text-xs text-fg-tertiary py-0.5">
                                                            <div className="w-1 h-1 rounded-full bg-fg-tertiary" />
                                                            <span>{getToolDescription(toolName)}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        }

                        // RESPONSE - BARK logo marker (bigger, with white background to hide line)
                        if (group.kind === 'response') {
                            // BAR-1270 — an external agent writing through the
                            // build-write channel must not wear BARK's face. Only
                            // this one stamped value changes the marker; anything
                            // else (including the unstamped legacy default) keeps
                            // the BARK logo exactly as before.
                            const isExternalAgent = group.item.author === 'external_agent';
                            // `on_behalf_of` is the human's report-library id. No
                            // id -> display-name map reaches this component (see
                            // UserMessage, which only knows the CURRENT user), so
                            // the id is shown verbatim rather than guessed at.
                            const onBehalfOf = group.item.onBehalfOf;
                            return (
                                <div key={`response-${groupIndex}`} className="relative flex items-start">
                                    {/* BARK logo for response - ~30px with white bg to cover the line */}
                                    <div className="relative z-10 w-[30px] h-[30px] flex-shrink-0 flex items-center justify-center bg-surface rounded-full -ml-[5px]">
                                        {isExternalAgent ? (
                                            <Bot
                                                className={`w-[18px] h-[18px] text-fg-secondary ${group.isStream ? 'animate-pulse' : ''}`}
                                                aria-label="External agent"
                                            />
                                        ) : (
                                            <img
                                                src="/Data/images/BARK_tree_2_navy.png"
                                                alt="BARK"
                                                className={`w-[30px] h-[30px] object-contain ${group.isStream ? 'animate-pulse' : ''}`}
                                            />
                                        )}
                                    </div>

                                    <div className="flex-1 ml-1">
                                        {isExternalAgent && (
                                            <div className="text-[11px] font-medium text-fg-tertiary mb-0.5">
                                                External agent{onBehalfOf ? ` · via ${onBehalfOf}` : ''}
                                            </div>
                                        )}
                                        {group.isStream ? (
                                            // Streaming: show bouncing dots instead of raw streamed
                                            // markdown (with visible **, ##, pipe tables etc.). Tokens
                                            // still accumulate in App.tsx refs; when _isStreaming
                                            // flips false, this branch swaps to the finalized
                                            // markdown render below. Dots style matches the
                                            // "BARK is working..." indicator used elsewhere in this
                                            // component.
                                            <div
                                                className="flex gap-0.5 py-2"
                                                role="status"
                                                aria-label="Writing response"
                                            >
                                                <div className="w-1.5 h-1.5 bg-secondary rounded-full animate-bounce" />
                                                <div className="w-1.5 h-1.5 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                                                <div className="w-1.5 h-1.5 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
                                            </div>
                                        ) : (
                                            // Finalized: render markdown (GFM tables, math, citations)
                                            <div className="text-fg font-normal leading-relaxed chat-message-text">
                                                <ChatMessageMarkdown
                                                    content={group.item.content}
                                                    onCitationClick={dispatchCitationClick}
                                                    onHandoffClick={dispatchHandoffClick}
                                                    onReportClick={onReportClick}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        }

                        // WORKSPACE SWITCH - centered banner in the timeline
                        if (group.kind === 'workspace_switch') {
                            const { to, targetNodeName, initiator } = group.data;
                            const wsLabels: Record<string, string> = { plan: 'Plan', institution_context: 'Institution context', research: 'Research', production: 'Build' };
                            const targetLabel = targetNodeName || wsLabels[to] || to;
                            const actor = initiator === 'agent' ? 'BARK' : 'You';
                            return (
                                <div key={`ws-switch-${groupIndex}`} className="flex justify-center my-2">
                                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-bg-warm border border-border-default rounded-full">
                                        <svg className="w-3.5 h-3.5 text-fg-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                                        </svg>
                                        <span className="text-xs font-medium text-fg-secondary">
                                            {actor} switched to {targetLabel}
                                        </span>
                                    </div>
                                </div>
                            );
                        }

                        // USER QUESTION - structured question card from ask_user_question tool
                        if (group.kind === 'user_question') {
                            const { question, question_type } = group.data;
                            const typeLabels: Record<string, string> = {
                                clarification: 'Clarification',
                                scope: 'Scope',
                                interpretation: 'Approach',
                                continue: 'Continue'
                            };
                            const badge = (question_type && typeLabels[question_type]) || 'Question';

                            // Check if the next item is a tool_result for ask_user_question (= answered)
                            const nextGroup = groupedItems[groupIndex + 1];
                            const isAnswered = nextGroup && nextGroup.kind === 'tools' &&
                                'items' in nextGroup && nextGroup.items?.some((t: ActivityEvent) =>
                                    t.type === 'tool_result' && t.content?.includes('ask_user_question'));
                            // Extract answer from tool_result summary if available
                            let answer: string | null = null;
                            if (isAnswered && 'items' in nextGroup) {
                                const resultItem = nextGroup.items?.find((t: ActivityEvent) =>
                                    t.type === 'tool_result' && t.content?.includes('ask_user_question'));
                                if (resultItem) {
                                    const match = resultItem.content.match(/User answered: "(.+?)"/);
                                    answer = match ? match[1] : null;
                                }
                            }

                            // Compact display — one-liner badge with question or answer
                            return (
                                <div key={`question-${groupIndex}`} className="flex items-start gap-2 my-1 ml-4">
                                    <span className="inline-block px-1.5 py-0.5 bg-accent text-white text-[10px] font-semibold rounded flex-shrink-0 mt-0.5">
                                        {badge}
                                    </span>
                                    {answer ? (
                                        <span className="text-xs text-fg-secondary">{answer}</span>
                                    ) : (
                                        <span className="text-xs text-fg-tertiary italic">{question}</span>
                                    )}
                                </div>
                            );
                        }

                        // DEEP RESEARCH PROGRESS - inline research pipeline progress
                        // Supports both live state (streaming) and persisted snapshots (from history)
                        if (group.kind === 'research_progress') {
                            const savedData = 'savedData' in group ? group.savedData : undefined;
                            let progressData: ResearchProgressState | null = null;
                            if (researchProgress && (researchProgress.isActive || researchProgress.phases.length > 0)) {
                                progressData = researchProgress;
                            } else if (savedData) {
                                try { progressData = JSON.parse(savedData); } catch { progressData = null; }
                            } else {
                                progressData = null;
                            }
                            if (progressData) {
                                return (
                                    <DeepResearchProgress
                                        key={`research-progress-${groupIndex}`}
                                        researchProgress={progressData}
                                    />
                                );
                            }
                            return null;
                        }

                        // HANDOFF PACKAGE - clickable indicator that opens the panel
                        if (group.kind === 'handoff_package' && handoffPackage) {
                            return (
                                <div key={`handoff-${groupIndex}`} className="relative flex items-start">
                                    <div className="relative z-10 w-[10px] h-[10px] rounded-full bg-accent border border-white shadow-sm flex-shrink-0 mt-0.5 ml-1" />
                                    <button
                                        onClick={onOpenHandoffPanel}
                                        className="flex items-center gap-2 ml-3 py-1 px-2 -ml-0.5 rounded hover:bg-accent-tint transition-colors group"
                                    >
                                        <span className="text-xs font-medium text-accent-hover">Handoff package</span>
                                        <span className="text-xs text-fg-tertiary">
                                            {handoffPackage.requirements.length} reqs, {handoffPackage.constraints.length} constraints, {handoffPackage.gaps.length} gaps
                                        </span>
                                        <svg className="w-3 h-3 text-accent group-hover:text-accent transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                    </button>
                                </div>
                            );
                        }

                        // LOADING indicator - shown when streaming but no content yet
                        if (group.kind === 'loading') {
                            return (
                                <div key={`loading-${groupIndex}`} className="relative flex items-start">
                                    <div className="relative z-10 w-[10px] h-[10px] rounded-full bg-border-default border border-white shadow-sm flex-shrink-0 mt-0.5 ml-1 animate-pulse" />
                                    <div className="flex items-center gap-2 ml-3 py-0.5">
                                        <span className="text-xs text-fg-tertiary">{group.message}</span>
                                    </div>
                                </div>
                            );
                        }

                        return null;
                    })}
                </div>
            </div>
        </div>
    );
}

function PlainChatMessageText({ content }: { content: string }) {
    return <span className="whitespace-pre-wrap break-words">{content}</span>;
}

// User message bubble — dark blue for own messages, white for others
function UserMessage({ msg, userData, compact }: { msg: any; userData: any; compact: boolean }) {
    const isMe = !msg.user_id || msg.user_id === userData?.email || (msg.user_name && msg.user_name === userData?.name);
    const displayName = msg.user_name || (isMe ? (userData?.name || 'You') : (msg.user_id?.split('@')[0] || 'User'));
    const uploadedFiles = Array.isArray(msg.uploadedFiles)
        ? msg.uploadedFiles
        : (Array.isArray(msg.metadata?.uploaded_files) ? msg.metadata.uploaded_files : []);
    return (
        <div className={`max-w-[80%] ${compact ? 'max-w-[90%]' : 'max-w-[70%]'}`}>
            <div className="text-right mb-0.5">
                <span className="text-[11px] font-medium text-fg-tertiary">{displayName}</span>
            </div>
            {/* User bubble: bg-primary (navy) is INTENTIONAL per palette-overhaul-2026-04
                corrective (plan 002). Plan 001's "chat bubbles stay teal" rule was
                explicitly retired by Nicholas in the corrective pass — bright teal
                bubbles clashed with the new navy chrome. Do not "restore" to bg-accent
                or bg-secondary; this is the documented final state. */}
            <div className={`rounded-xl px-3 py-1.5 chat-message-text ${
                isMe ? 'bg-primary text-on-primary' : 'bg-bg-warm text-fg'
            }`}>
                <div className="font-normal leading-snug">
                    <PlainChatMessageText content={msg.message || ''} />
                </div>
                {uploadedFiles.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                        {uploadedFiles.map((file: any) => (
                            <span
                                key={file.upload_id || file.display_name}
                                className="inline-flex items-center gap-1 max-w-full px-1.5 py-0.5 rounded bg-white/15 text-[11px]"
                                title={file.display_name || file.original_filename || file.upload_id}
                            >
                                <FileText size={11} className="flex-shrink-0" />
                                <span className="truncate max-w-[180px]">
                                    {file.display_name || file.original_filename || file.upload_id}
                                </span>
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

const GeminiChatArea = ({
    history,
    onSendMessage,
    isLoading,
    streamingStatus,
    streamingResponse,
    streamingThinking,
    streamingActivityLog,
    activeToolCall,
    onCancelWorkflow,
    activeChat,
    compact,
    onServiceSelect,
    currentReport,
    onCloseReport,
    setActiveView,
    userData,
    isCanvasMode,
    setIsCanvasMode,
    researchMission,
    hasReconnectedSession,
    tokenUsage,
    researchProgress,
    getResearchProgress,
    activeResearchTrackingId,
    handoffPackage,
    onOpenHandoffPanel,
    pendingQuestion,
    onAnswerQuestion,
    onReportClick,
    initialInput,
    autoSendOnMount,
}: GeminiChatAreaProps) => {
    const [newMessage, setNewMessage] = useState(initialInput || '');
    const [selectedOption, setSelectedOption] = useState<number | null>(null);
    const [otherText, setOtherText] = useState('');
    const [researchDepth, setResearchDepth] = useState('Standard');
    const [useMultiStageValidation, setUseMultiStageValidation] = useState(false);
    const [autoAcceptPlans, setAutoAcceptPlans] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [feedbackTarget, setFeedbackTarget] = useState<FeedbackTarget | null>(null);
    const [feedbackInitialSentiment, setFeedbackInitialSentiment] = useState<FeedbackSentiment>(null);
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadConfig, setUploadConfig] = useState<ChatUploadConfig>(DEFAULT_CHAT_UPLOAD_CONFIG);
    const chatLogRef = useRef<HTMLDivElement>(null);
    const settingsRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const feedbackSubmittingRef = useRef(false);
    const activeProject = useProjectStore(s => s.activeProject);
    const barkWorkspace = useProjectStore(s => s.barkWorkspace);
    const dictWorkspace = useProjectStore(s => s.dictWorkspace);
    const loadSpecificWorkspace = useProjectStore(s => s.loadSpecificWorkspace);
    const addLocalEvent = useProjectStore(s => s.addLocalEvent);
    // Opening a project loads only the newest page of history, so a long
    // project has turns above the top of this pane until the user asks.
    const oldestLoadedSequence = useProjectStore(s => s.oldestLoadedSequence);
    const earlierEventsLoading = useProjectStore(s => s.earlierEventsLoading);
    const loadEarlierEvents = useProjectStore(s => s.loadEarlierEvents);
    const hasEarlierEvents = oldestLoadedSequence > 1;

    useEffect(() => {
        let cancelled = false;

        const loadUploadConfig = async () => {
            const config = await loadChatUploadConfig();
            if (cancelled) return;
            setUploadConfig(config);
        };

        loadUploadConfig();
        return () => {
            cancelled = true;
        };
    }, []);

    // Auto-send the seed message on mount when the parent opts in via
    // `autoSendOnMount`. Parent (App.tsx) forces a remount per project via
    // `key={activeProject.project_id}` on <GeminiChatArea>, so this effect
    // naturally fires once per project creation — no cross-instance ref
    // machinery needed. The ref here is only to defuse React strict mode's
    // intentional double-invocation of effects in dev.
    const autoSentRef = useRef(false);
    useEffect(() => {
        if (autoSentRef.current) return;
        if (!autoSendOnMount) return;
        const seed = (initialInput || '').trim();
        if (!seed) return;
        autoSentRef.current = true;
        // Clear the textarea so the seed doesn't also sit in the input.
        setNewMessage('');
        // BAR-523: a project started with attached files stashes them in the
        // store keyed by project id; consume + attach them to this first turn so
        // the opening message carries the uploads (matching the manual attach).
        const projectId = useProjectStore.getState().activeProject?.project_id;
        const firstTurnFiles = projectId
            ? useProjectStore.getState().consumeFirstTurnUploads(projectId)
            : [];
        if (firstTurnFiles.length > 0) {
            onSendMessage(seed, researchDepth, useMultiStageValidation, {
                displayMessage: seed,
                uploadedFiles: firstTurnFiles,
                clientMessageId: crypto.randomUUID?.() || `msg_${Date.now()}`,
            });
        } else {
            onSendMessage(seed, researchDepth, useMultiStageValidation);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const depthOptions = ['Quick', 'Standard', 'Long'];
    const hasMessages = history.length > 0;

    // =========================================================================
    // SINGLE SOURCE OF TRUTH: Read ONLY from chat history
    // All streaming content is written to chat history in real-time by App.tsx
    // =========================================================================
    const lastMessage = hasMessages ? history[history.length - 1] : null;
    const lastMessageIsBark = lastMessage?.sender === 'bark';

    // For finalized messages: read from the message's own activityLog (persisted via Firestore)
    // For streaming messages: use streamingActivityLog prop (accumulated in activityLogRef by App.tsx)
    // The streaming message's .activityLog is always [] (never updated during streaming),
    // so we must use the prop that App.tsx maintains via setActivityLog().
    const isStreamingMessage = lastMessage && (lastMessage as any)?._isStreaming;
    const activityLog: StreamingActivityEvent[] = isStreamingMessage
        ? (streamingActivityLog || [])
        : ((lastMessageIsBark && (lastMessage as any)?.activityLog) || []);

    // Show streaming UI if loading OR if last BARK message has activity
    const showStreamingUI = isLoading || (lastMessageIsBark && activityLog.length > 0);

    // Follow the answer to the end while it streams, until the user scrolls
    // up to read; following resumes at the end, or when they send.
    const { pin: pinChatLog, jump: jumpChatLog, props: followProps } = useFollowLatest(chatLogRef);

    useEffect(pinChatLog, [history, streamingResponse, streamingActivityLog, pinChatLog]);

    // Close settings dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
                setIsSettingsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Citation and handoff chip clicks are dispatched via the module-level
    // `dispatchCitationClick` / `dispatchHandoffClick` constants (see top of
    // file). The previous DOM-delegation listener that scanned for
    // data-marker-id / data-handoff-id attributes is no longer needed —
    // those attributes aren't produced by ChatMessageMarkdown.

    const handleFileSelection = (files: FileList | null) => {
        if (!files) return;
        setUploadError(null);
        const accepted: File[] = [];
        const activeUploadCount = Object.values(dictWorkspace?.uploaded_files || {}).filter(
            uploadedFile => uploadedFile.upload_status !== 'deleted',
        ).length;
        for (const file of Array.from(files)) {
            if (activeUploadCount + pendingFiles.length + accepted.length >= uploadConfig.maxFilesPerWorkspace) {
                setUploadError(`This workspace can hold up to ${uploadConfig.maxFilesPerWorkspace} uploaded files.`);
                continue;
            }
            if (file.size > uploadConfig.maxBytes) {
                setUploadError(`${file.name} is larger than ${formatFileSize(uploadConfig.maxBytes)}.`);
                continue;
            }
            if (!isSupportedUploadFile(file, uploadConfig)) {
                setUploadError(`${file.name} is not a supported file type.`);
                continue;
            }
            accepted.push(file);
        }
        if (accepted.length) {
            setPendingFiles((current) => [...current, ...accepted]);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const removePendingFile = (index: number) => {
        setPendingFiles((current) => current.filter((_, i) => i !== index));
        setUploadError(null);
    };

    // The refresh after an upload only re-reads the workspace so its file list
    // is current. It must never throw into handleSubmit: the files are stored
    // by then, and a throw there was taken for a failed upload and deleted
    // them (BAR-1585).
    const refreshWorkspaceAfterUpload = async (workspaceId: string, userId: string) => {
        try {
            // fresh: post-write reads must not join a pre-write /state
            // request still in flight (BAR-248).
            await loadSpecificWorkspace(workspaceId, userId, { showLoading: false, fresh: true });
        } catch (error) {
            console.warn('[chat] workspace refresh after an upload failed', { workspaceId, error });
        }
    };

    const handleSubmit = async (e: React.SyntheticEvent) => {
        e.preventDefault();
        if (isLoading || isUploading) return;
        const trimmedMessage = newMessage.trim();
        if (!trimmedMessage && pendingFiles.length === 0) return;

        let uploadedFiles: UploadedWorkspaceFile[] = [];
        const clientMessageId = pendingFiles.length > 0
            ? (crypto.randomUUID?.() || `msg_${Date.now()}`)
            : undefined;
        if (pendingFiles.length > 0) {
            const userId = userData?.email;
            const projectId = activeProject?.project_id;
            const workspaceId = dictWorkspace?.workspace_id || (projectId ? `ws_${projectId}` : null);
            if (!userId || !projectId || !workspaceId) {
                setUploadError('Open a project workspace before uploading files.');
                return;
            }

            setIsUploading(true);
            setUploadError(null);
            try {
                for (const file of pendingFiles) {
                    // BAR-308: pass the threshold so uploadWorkspaceFile routes
                    // large files through the signed-URL direct-to-GCS flow.
                    const result = await uploadWorkspaceFile(
                        workspaceId,
                        userId,
                        projectId,
                        file,
                        file.name,
                        clientMessageId,
                        { multipart_max_bytes: uploadConfig.multipartMaxBytes },
                    );
                    if (!result.success || !result.uploaded_file) {
                        throw new Error(result.error || `Failed to upload ${file.name}`);
                    }
                    uploadedFiles.push(result.uploaded_file);
                }
                await refreshWorkspaceAfterUpload(workspaceId, userId);
            } catch (error: any) {
                if (uploadedFiles.length > 0) {
                    await Promise.allSettled(
                        uploadedFiles.map((uploadedFile) => (
                            deleteWorkspaceUpload(workspaceId, userId, uploadedFile.upload_id)
                        )),
                    );
                    await refreshWorkspaceAfterUpload(workspaceId, userId);
                }
                const rollbackSuffix = uploadedFiles.length > 0
                    ? ' Already-uploaded files were removed; please try again.'
                    : '';
                setUploadError(`${error.message || 'Upload failed.'}${rollbackSuffix}`);
                return;
            } finally {
                setIsUploading(false);
            }
        }

        const displayMessage = trimmedMessage || 'Uploaded files.';
        jumpChatLog();
        onSendMessage(displayMessage, researchDepth, useMultiStageValidation, {
            displayMessage,
            uploadedFiles,
            clientMessageId,
        });
        setNewMessage('');
        setPendingFiles([]);
        setUploadError(null);
    };

    const openFeedbackForMessage = (msg: any, sentiment: FeedbackSentiment) => {
        if (!msg?.messageId) return;
        const targetSnippet = makeFeedbackSnippet(getFeedbackTargetText(msg));
        if (!targetSnippet) return;
        setFeedbackTarget({
            messageId: msg.messageId,
            targetSnippet,
        });
        setFeedbackInitialSentiment(sentiment);
    };

    const handleFeedbackSubmit = async ({ sentiment, comment }: { sentiment: FeedbackSentiment; comment: string }) => {
        if (feedbackSubmittingRef.current) return;
        feedbackSubmittingRef.current = true;
        try {
            if (!activeProject || !feedbackTarget) {
                throw new Error('Feedback target is no longer available');
            }

            const trimmedComment = comment.trim();
            const workspaceType = barkWorkspace || (activeProject as any).bark_workspace || (activeProject as any).project_type || '';
            const result = await submitMessageFeedback(activeProject.project_id, {
                target_message_id: feedbackTarget.messageId,
                sentiment,
                workspace_type: workspaceType,
                comment: trimmedComment,
                project_name: activeProject.name || '',
                target_snippet: feedbackTarget.targetSnippet,
            });

            if (!result.success) {
                throw new Error(result.error || 'Feedback could not be sent');
            }

            const timestamp = result.timestamp || new Date().toISOString();
            addLocalEvent({
                event_id: result.event_id,
                sequence: result.sequence,
                project_id: activeProject.project_id,
                message_id: feedbackTarget.messageId,
                type: 'feedback',
                content: trimmedComment,
                timestamp,
                metadata: {
                    feedback_id: result.feedback_id,
                    target_message_id: feedbackTarget.messageId,
                    sentiment,
                    workspace_type: workspaceType,
                    project_name: activeProject.name || '',
                    target_snippet: feedbackTarget.targetSnippet,
                },
            });

            setFeedbackTarget(null);
            setFeedbackInitialSentiment(null);
        } finally {
            feedbackSubmittingRef.current = false;
        }
    };

    const canSubmit = (!!newMessage.trim() || pendingFiles.length > 0) && !isLoading && !isUploading;

    const renderAttachmentButton = () => (
        <>
            <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                accept={CHAT_UPLOAD_ACCEPT}
                onChange={(event) => handleFileSelection(event.target.files)}
            />
            <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading || isUploading}
                className="pl-1 pr-1.5 py-1.5 rounded hover:bg-bg-warm text-fg-tertiary hover:text-fg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Attach file"
                aria-label="Attach file"
            >
                <Paperclip size={16} strokeWidth={1.8} />
            </button>
        </>
    );

    const renderPendingFileChips = () => (
        (pendingFiles.length > 0 || uploadError) ? (
            <div className="px-4 pb-2 flex flex-wrap gap-1.5">
                {pendingFiles.map((file, index) => (
                    <div
                        key={`${file.name}-${file.size}-${index}`}
                        className="inline-flex items-center gap-1.5 max-w-full px-2 py-1 rounded border border-border-default bg-bg-warm text-[11px] text-fg-secondary"
                        title={`${file.name} (${formatFileSize(file.size)})`}
                    >
                        <FileText size={12} className="text-fg-tertiary flex-shrink-0" />
                        <span className="truncate max-w-[220px]">{file.name}</span>
                        <span className="text-fg-tertiary flex-shrink-0">{formatFileSize(file.size)}</span>
                        <button
                            type="button"
                            onClick={() => removePendingFile(index)}
                            className="p-0.5 rounded hover:bg-border-default text-fg-tertiary"
                            title="Remove file"
                            aria-label={`Remove ${file.name}`}
                        >
                            <X size={12} />
                        </button>
                    </div>
                ))}
                {isUploading && (
                    <span className="inline-flex items-center px-2 py-1 rounded bg-accent-tint text-accent-hover text-[11px] font-medium">
                        Uploading...
                    </span>
                )}
                {uploadError && (
                    <span className="basis-full text-[11px] text-danger">
                        {uploadError}
                    </span>
                )}
            </div>
        ) : null
    );

    if (!hasMessages && !compact) {
        // Centered welcome state - everything centered in viewport with personalized greeting
        return (
            <div className="flex flex-col items-center justify-center h-full bg-surface">
                <div className="text-center max-w-2xl mx-auto px-6">
                    {/* Welcome Message */}
                    <div className="mb-12">
                        <h1 className="text-4xl font-semibold text-fg mb-4">
                            Hello {userData?.name || userData?.email?.split('@')[0] || 'there'}, how can I help you?
                        </h1>
                        <p className="text-lg text-fg-secondary">I'm your regulatory expert assistant</p>
                    </div>

                    {/* Centered Input - Two Row Design */}
                    <form onSubmit={handleSubmit} className="w-full max-w-3xl mx-auto">
                        <div className="bg-surface border border-border-default rounded-xl shadow-sm focus-within:border-secondary focus-within:ring-2 focus-within:ring-secondary/20 transition-all duration-200">
                            {/* Top Row - Text Input */}
                            <textarea
                                value={newMessage}
                                onChange={(e) => {
                                    setNewMessage(e.target.value);
                                    // Auto-resize textarea
                                    e.target.style.height = 'auto';
                                    e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
                                }}
                                placeholder="Message BARK..."
                                rows={1}
                                className="w-full resize-none border-none px-4 pt-3 pb-1 focus:outline-none chat-input-text text-fg placeholder-fg-tertiary bg-transparent"
                                style={{ minHeight: '60px', maxHeight: '150px' }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSubmit(e);
                                    }
                                }}
                                disabled={isLoading}
                            />

                            {renderPendingFileChips()}
                            
                            {/* Bottom Row - Controls */}
                            <div
                                data-testid="chat-composer-footer"
                                className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 px-3 pb-2.5 pt-1 border-t border-divider"
                            >
                                <div
                                    data-testid="chat-composer-footer-left"
                                    className="justify-self-start flex min-w-0 items-center gap-3"
                                >
                                    {renderAttachmentButton()}
                                    <div className="flex min-w-0 items-center gap-3 overflow-hidden">
                                        <WorkspaceIndicator />
                                        <ContextWindowBar tokenUsage={tokenUsage} />
                                    </div>
                                    {/* Settings Menu (temporarily hidden) */}
                                    <div className="relative hidden" ref={settingsRef}>
                                    <button
                                        type="button"
                                        onClick={() => setIsSettingsOpen(!isSettingsOpen)}
                                        className="p-1.5 rounded hover:bg-bg-warm text-fg-tertiary hover:text-fg-secondary transition-colors"
                                        title="Research settings"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path>
                                            <circle cx="12" cy="12" r="3"></circle>
                                        </svg>
                                    </button>

                                    {/* Settings Dropdown Menu */}
                                    {isSettingsOpen && (
                                        <div className="absolute left-0 bottom-full mb-2 bg-surface border border-border-default rounded-lg shadow-lg overflow-hidden z-10 w-64">
                                            {/* Research Depth Section */}
                                            <div className="px-3 py-2 border-b border-divider">
                                                <label className="text-xs font-medium text-fg-secondary mb-2 block">Research depth</label>
                                                <div className="flex gap-1">
                                                    {depthOptions.map(option => (
                                                        <button
                                                            key={option}
                                                            type="button"
                                                            onClick={() => setResearchDepth(option)}
                                                            className={`flex-1 px-2 py-1.5 text-xs rounded transition-colors ${
                                                                researchDepth === option 
                                                                    ? 'bg-accent text-white font-medium' 
                                                                    : 'bg-bg-warm text-fg-secondary hover:bg-bg-warm'
                                                            }`}
                                                        >
                                                            {option}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            
                                            {/* Multi-Stage Validation Section */}
                                            <div className="px-3 py-2 border-b border-divider">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex-1">
                                                        <label className="text-xs font-medium text-fg-secondary block">Multi-stage validation</label>
                                                        <p className="text-xs text-fg-tertiary mt-0.5">6 AI validators, up to 30 min</p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => setUseMultiStageValidation(!useMultiStageValidation)}
                                                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ml-3 ${
                                                            useMultiStageValidation ? 'bg-accent' : 'bg-border-default'
                                                        }`}
                                                    >
                                                        <span
                                                            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-surface transition-transform duration-200 ${
                                                                useMultiStageValidation ? 'translate-x-5' : 'translate-x-0.5'
                                                            }`}
                                                        />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Auto-Approve Section (hypotheses + plans) */}
                                            <div className="px-3 py-2">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex-1">
                                                        <label className="text-xs font-medium text-fg-secondary block">Auto-Approve</label>
                                                        <p className="text-xs text-fg-tertiary mt-0.5">Auto-approve hypotheses & plans after 20s</p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => setAutoAcceptPlans(!autoAcceptPlans)}
                                                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ml-3 ${
                                                            autoAcceptPlans ? 'bg-accent' : 'bg-border-default'
                                                        }`}
                                                    >
                                                        <span
                                                            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-surface transition-transform duration-200 ${
                                                                autoAcceptPlans ? 'translate-x-5' : 'translate-x-0.5'
                                                            }`}
                                                        />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                </div>

                                {/* Right Side - Send Button */}
                                <div
                                    data-testid="chat-composer-footer-right"
                                    className="justify-self-end flex items-center"
                                >
                                    <button
                                        type="submit"
                                        disabled={!canSubmit}
                                        className="p-1.5 rounded bg-accent-copper text-white hover:opacity-90 disabled:bg-border-default disabled:cursor-not-allowed transition-all"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="22" y1="2" x2="11" y2="13"></line>
                                            <polygon points="22,2 15,22 11,13 2,9"></polygon>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    // Chat view - input at bottom after messages exist
    return (
        <div className="flex flex-col h-full bg-surface w-full">
            {/* Chat Messages - Takes remaining space with scroll */}
            <div
                ref={chatLogRef}
                className={`flex-1 overflow-y-auto w-full bg-surface min-h-0 chat-scrollbar`}
                {...followProps}
            >
                <div className={`${compact ? 'p-3' : 'max-w-4xl mx-auto p-4'} space-y-3`}>
                    {/* Sticky Header - Research Mission */}
                    {researchMission && (
                        <div className="sticky top-0 z-10 bg-surface pb-2 space-y-2">
                            <ResearchMissionPanel mission={researchMission} />
                        </div>
                    )}

                    {hasEarlierEvents && (
                        <div className="w-full flex justify-center pb-1">
                            <button
                                type="button"
                                onClick={() => {
                                    const projectId = activeProject?.project_id;
                                    const userId = userData?.email;
                                    if (projectId && userId) void loadEarlierEvents(projectId, userId);
                                }}
                                disabled={earlierEventsLoading}
                                // min-h-[32px]: UX Standard U-10 hit-target floor.
                                // 12px text at leading-normal is 18px of content,
                                // so padding alone leaves this at 28px.
                                className="inline-flex items-center min-h-[32px] px-3 py-1 text-[12px] text-fg-tertiary bg-bg-warm border border-border-default rounded-full hover:text-fg disabled:opacity-60"
                            >
                                {earlierEventsLoading ? 'Loading…' : 'Load earlier messages'}
                            </button>
                        </div>
                    )}

                    {history.map((msg: any, index: number) => {
                        // Workspace switch events — render as full-width centered divider banner
                        if (msg.message && typeof msg.message === 'string' && msg.message.startsWith('[workspace_switch]')) {
                            const text = msg.message.replace('[workspace_switch] ', '');
                            return (
                                <div key={index} className="w-full flex items-center gap-3 my-3">
                                    <div className="flex-1 h-px bg-bg-warm" />
                                    <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-bg-warm border border-border-default rounded-full flex-shrink-0">
                                        <svg className="w-3 h-3 text-fg-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                                        </svg>
                                        <span className="text-xs font-medium text-fg-tertiary">{text}</span>
                                    </div>
                                    <div className="flex-1 h-px bg-bg-warm" />
                                </div>
                            );
                        }

                        return (
                        <div key={index} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                            {msg.sender === 'user' ? (
                                <UserMessage msg={msg} userData={userData} compact={compact} />
                            ) : (
                                // BARK Message - UNIFIED TIMELINE (no outer logo wrapper - BARK logo is IN the timeline)
                                <div className={`max-w-[80%] ${compact ? 'max-w-[90%]' : 'max-w-[70%]'}`}>
                                    {/* Skip rendering for streaming message - it's rendered separately in the chronological timeline */}
                                    {msg._isStreaming ? null : (
                                        /* UNIFIED TIMELINE: If activityLog exists, render ONLY the timeline */
                                        msg.activityLog ? (
                                            <InlineActivityTimeline
                                                thinking={msg.thinking}
                                                activityLog={msg.activityLog}
                                                message={msg.message}
                                                researchProgress={(() => {
                                                    // Primary: look up by message's own messageId (from Firestore events)
                                                    if (msg.messageId && getResearchProgress) return getResearchProgress(msg.messageId);
                                                    // Fallback: if this message has a research tool call, use activeResearchTrackingId
                                                    if (activeResearchTrackingId && getResearchProgress && msg.activityLog?.some(
                                                        (item: any) => item.type === 'tool_call' && (
                                                            item.content?.includes('research_workflow') || item.content?.includes('research workflow')
                                                        )
                                                    )) return getResearchProgress(activeResearchTrackingId);
                                                    return undefined;
                                                })()}
                                                onReportClick={onReportClick}
                                            />
                                        ) : (
                                            /* Legacy: If no activityLog, show message directly with BARK logo */
                                            msg.message && (
                                                <div className="flex items-start gap-1">
                                                    <div className="w-[30px] h-[30px] flex-shrink-0 flex items-center justify-center bg-surface rounded-full">
                                                        <img
                                                            src="/Data/images/BARK_tree_2_navy.png"
                                                            alt="BARK"
                                                            className="w-[30px] h-[30px] object-contain"
                                                        />
                                                    </div>
                                                    <div className="text-fg font-normal leading-relaxed chat-message-text">
                                                        <ChatMessageMarkdown
                                                            content={msg.message}
                                                            onCitationClick={dispatchCitationClick}
                                                            onHandoffClick={dispatchHandoffClick}
                                                            onReportClick={onReportClick}
                                                        />
                                                    </div>
                                                </div>
                                            )
                                        )
                                    )}
                                    {/* Render proposed plan as a separate component */}
                                    {msg.proposed_plan && (
                                        <ProposedPlan
                                            plan={msg.proposed_plan}
                                            isAccepted={index < history.length - 1 && history[index + 1]?.sender === 'user'}
                                            autoAccept={autoAcceptPlans && index === history.length - 1}
                                            isLoading={isLoading}
                                            onAccept={() => {
                                                if (!isLoading) {
                                                    onSendMessage("yes", researchDepth, useMultiStageValidation);
                                                }
                                            }}
                                            onReject={() => {
                                                // Focus the input box for user to type modifications
                                                const textarea = document.querySelector('textarea') as HTMLTextAreaElement;
                                                if (textarea) {
                                                    textarea.focus();
                                                }
                                            }}
                                        />
                                    )}
                                    {!msg._isStreaming && (
                                        <div className="mt-2 ml-[31px] flex flex-wrap items-center gap-3">
                                            {/* Source icons first, then the feedback buttons, on one line */}
                                            <MessageSourceIcons sources={msg.sources_used} />
                                            {msg.messageId && makeFeedbackSnippet(getFeedbackTargetText(msg)) && (
                                                <MessageFeedbackControls
                                                    feedbackProvided={msg.feedbackProvided}
                                                    onOpenFeedback={(sentiment) => openFeedbackForMessage(msg, sentiment)}
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        );
                    })}
                    
                    {/* STREAMING TIMELINE - Uses unified InlineActivityTimeline component */}
                    {/* Shows both finalized events from activityLog AND live streaming content */}
                    {/* Also renders when research is active (researchProgress.isActive) to survive complete_turn gaps */}
                    {(showStreamingUI && lastMessage?._isStreaming) || (researchProgress && researchProgress.isActive) ? (
                        <div className="max-w-[80%]">
                            <InlineActivityTimeline
                                thinking={undefined}
                                activityLog={activityLog}
                                // Pass streaming props for live content
                                streamingThinking={streamingThinking}
                                streamingResponse={streamingResponse}
                                activeToolCall={activeToolCall}
                                isStreaming={isLoading}
                                streamingStatus={streamingStatus}
                                researchProgress={researchProgress}
                                handoffPackage={handoffPackage}
                                onOpenHandoffPanel={onOpenHandoffPanel}
                                onReportClick={onReportClick}
                            />
                        </div>
                    ) : null}

                    {/* Active Session Indicator - shows when reconnected to working agent */}
                    {hasReconnectedSession && !showStreamingUI && (
                        <div className="max-w-[80%] flex items-center gap-3 py-3">
                            <div className="flex items-center gap-2">
                                <img
                                    src="/Data/images/BARK_tree_2_navy.png"
                                    alt="BARK"
                                    className="w-[30px] h-[30px] object-contain"
                                />
                                <div className="flex items-center gap-2 text-fg-secondary">
                                    <div className="flex gap-0.5">
                                        <div className="w-1.5 h-1.5 bg-secondary rounded-full animate-bounce"></div>
                                        <div className="w-1.5 h-1.5 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></div>
                                        <div className="w-1.5 h-1.5 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
                                    </div>
                                    <span className="text-sm font-medium text-fg-secondary">BARK is working...</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Tool call indicator */}
                    {activeToolCall && (
                                <div className="flex items-center space-x-2 text-sm text-accent">
                                    <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span>Using {activeToolCall}...</span>
                                </div>
                            )}
                        </div>
                </div>

            {/* Input Area - Fixed at bottom */}
            <div className={`${compact ? 'p-3' : 'p-4'} bg-surface flex-shrink-0`}>
                {/* Question Form — replaces chat input when ask_user_question is pending */}
                {pendingQuestion && onAnswerQuestion ? (
                    <div className="max-w-4xl mx-auto">
                        <div className="bg-surface border border-border-default rounded-xl shadow-sm overflow-hidden">
                            {/* Header badge */}
                            <div className="px-4 pt-3 pb-0 flex items-center justify-between">
                                <span className="inline-block px-2 py-0.5 bg-accent text-white text-[10px] font-semibold rounded tracking-wide">
                                    {pendingQuestion.question_type === 'interpretation' ? 'Approach' :
                                     pendingQuestion.question_type === 'scope' ? 'Scope' :
                                     pendingQuestion.question_type === 'continue' ? 'Continue' : 'Clarification'}
                                </span>
                            </div>
                            {/* Question */}
                            <div className="px-4 pt-2.5 pb-1">
                                <p className="text-[13px] font-semibold text-fg leading-snug">{pendingQuestion.question}</p>
                            </div>
                            {/* Context */}
                            {pendingQuestion.context && (
                                <div className="px-4 pb-2">
                                    <p className="text-xs text-fg-tertiary leading-relaxed">{pendingQuestion.context}</p>
                                </div>
                            )}
                            {/* Options or free text input */}
                            {pendingQuestion.options && pendingQuestion.options.length > 0 ? (
                                <div className="px-3 pb-1">
                                    {pendingQuestion.options.map((opt: string, i: number) => (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={() => { setSelectedOption(i); setOtherText(''); }}
                                            className={`flex items-start gap-2.5 w-full text-left px-2 py-2 rounded-md transition-colors ${
                                                selectedOption === i
                                                    ? 'bg-accent-tint border border-accent-border'
                                                    : 'hover:bg-bg-warm border border-transparent'
                                            }`}
                                        >
                                            <div className={`mt-0.5 w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                                                selectedOption === i ? 'border-accent' : 'border-border-default'
                                            }`}>
                                                {selectedOption === i && <div className="w-1.5 h-1.5 rounded-full bg-accent" />}
                                            </div>
                                            <span className={`text-[13px] leading-snug ${
                                                selectedOption === i ? 'text-fg font-medium' : 'text-fg-secondary'
                                            }`}>{opt}</span>
                                        </button>
                                    ))}
                                    {/* Other option */}
                                    <button
                                        type="button"
                                        onClick={() => { setSelectedOption(-1); }}
                                        className={`flex items-start gap-2.5 w-full text-left px-2 py-2 rounded-md transition-colors ${
                                            selectedOption === -1
                                                ? 'bg-accent-tint border border-accent-border'
                                                : 'hover:bg-bg-warm border border-transparent'
                                        }`}
                                    >
                                        <div className={`mt-0.5 w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                                            selectedOption === -1 ? 'border-accent' : 'border-border-default'
                                        }`}>
                                            {selectedOption === -1 && <div className="w-1.5 h-1.5 rounded-full bg-accent" />}
                                        </div>
                                        <span className={`text-[13px] leading-snug ${
                                            selectedOption === -1 ? 'text-fg font-medium' : 'text-fg-secondary'
                                        }`}>Other</span>
                                    </button>
                                    {selectedOption === -1 && (
                                        <div className="px-2 pb-1">
                                            <input
                                                type="text"
                                                value={otherText}
                                                onChange={(e) => setOtherText(e.target.value)}
                                                placeholder="Type your answer..."
                                                className="w-full px-3 py-2 text-[13px] border border-border-default rounded-md focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/20"
                                                autoFocus
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && otherText.trim()) {
                                                        e.preventDefault();
                                                        onAnswerQuestion(otherText.trim());
                                                        setSelectedOption(null);
                                                        setOtherText('');
                                                    }
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            ) : (
                                /* No predefined options — show free text input directly */
                                <div className="px-4 pb-2">
                                    <input
                                        type="text"
                                        value={otherText}
                                        onChange={(e) => setOtherText(e.target.value)}
                                        placeholder="Type your answer..."
                                        className="w-full px-3 py-2 text-[13px] border border-border-default rounded-md focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/20"
                                        autoFocus
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && otherText.trim()) {
                                                e.preventDefault();
                                                onAnswerQuestion(otherText.trim());
                                                setOtherText('');
                                            }
                                        }}
                                    />
                                </div>
                            )}
                            {/* Submit button */}
                            <div className="px-4 py-2.5 border-t border-divider">
                                <button
                                    type="button"
                                    onClick={() => {
                                        const hasOptions = pendingQuestion.options && pendingQuestion.options.length > 0;
                                        if (!hasOptions && otherText.trim()) {
                                            // No options mode — just send the text
                                            onAnswerQuestion(otherText.trim());
                                        } else if (selectedOption === -1 && otherText.trim()) {
                                            onAnswerQuestion(otherText.trim());
                                        } else if (selectedOption !== null && selectedOption >= 0 && pendingQuestion.options) {
                                            onAnswerQuestion(pendingQuestion.options[selectedOption]);
                                        }
                                        setSelectedOption(null);
                                        setOtherText('');
                                    }}
                                    disabled={
                                        pendingQuestion.options && pendingQuestion.options.length > 0
                                            ? (selectedOption === null || (selectedOption === -1 && !otherText.trim()))
                                            : !otherText.trim()
                                    }
                                    className="w-full py-2 bg-accent text-white text-[13px] font-medium rounded-md hover:bg-accent-hover disabled:bg-bg-warm disabled:text-fg-tertiary disabled:cursor-not-allowed transition-colors"
                                >
                                    Submit answer
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
                    <div className="bg-surface border border-border-default rounded-xl shadow-sm focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/20 transition-all duration-200">
                        {/* Top Row - Text Input */}
                        <textarea
                            value={newMessage}
                            onChange={(e) => {
                                setNewMessage(e.target.value);
                                // Auto-resize textarea
                                e.target.style.height = 'auto';
                                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                            }}
                            placeholder={isLoading ? "Type your next message..." : "Message BARK..."}
                            rows={1}
                            className="w-full resize-none border-none px-4 pt-3 pb-1 focus:outline-none chat-input-text text-fg placeholder-fg-tertiary bg-transparent"
                            style={{ minHeight: '50px', maxHeight: '120px' }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    if (!isLoading) {
                                        handleSubmit(e);
                                    }
                                }
                            }}
                        />

                        {renderPendingFileChips()}
                        
                        {/* Bottom Row - Controls */}
                        <div
                            data-testid="chat-composer-footer"
                            className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 px-3 pb-2.5 pt-1 border-t border-divider"
                        >
                            <div
                                data-testid="chat-composer-footer-left"
                                className="justify-self-start flex min-w-0 items-center gap-3"
                            >
                                {renderAttachmentButton()}
                                <div className="flex min-w-0 items-center gap-3 overflow-hidden">
                                    <WorkspaceIndicator />
                                    <ContextWindowBar tokenUsage={tokenUsage} />
                                </div>
                                {/* Settings Menu (temporarily hidden) */}
                                <div className="relative hidden" ref={settingsRef}>
                                    <button
                                        type="button"
                                        onClick={() => setIsSettingsOpen(!isSettingsOpen)}
                                        className="p-1.5 rounded hover:bg-bg-warm text-fg-tertiary hover:text-fg-secondary transition-colors"
                                        title="Research settings"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path>
                                            <circle cx="12" cy="12" r="3"></circle>
                                        </svg>
                                    </button>

                                {/* Settings Dropdown Menu */}
                                {isSettingsOpen && (
                                    <div className="absolute left-0 bottom-full mb-2 bg-surface border border-border-default rounded-lg shadow-lg overflow-hidden z-10 w-64">
                                        {/* Research Depth Section */}
                                        <div className="px-3 py-2 border-b border-divider">
                                            <label className="text-xs font-medium text-fg-secondary mb-2 block">Research depth</label>
                                            <div className="flex gap-1">
                                                {depthOptions.map(option => (
                                                    <button
                                                        key={option}
                                                        type="button"
                                                        onClick={() => setResearchDepth(option)}
                                                        className={`flex-1 px-2 py-1.5 text-xs rounded transition-colors ${
                                                            researchDepth === option 
                                                                ? 'bg-accent text-white font-medium' 
                                                                : 'bg-bg-warm text-fg-secondary hover:bg-bg-warm'
                                                        }`}
                                                    >
                                                        {option}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        
                                        {/* Multi-Stage Validation Section */}
                                        <div className="px-3 py-2 border-b border-divider">
                                            <div className="flex items-center justify-between">
                                                <div className="flex-1">
                                                    <label className="text-xs font-medium text-fg-secondary block">Multi-stage validation</label>
                                                    <p className="text-xs text-fg-tertiary mt-0.5">6 AI validators, up to 30 min</p>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => setUseMultiStageValidation(!useMultiStageValidation)}
                                                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ml-3 ${
                                                        useMultiStageValidation ? 'bg-accent' : 'bg-border-default'
                                                    }`}
                                                >
                                                    <span
                                                        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-surface transition-transform duration-200 ${
                                                            useMultiStageValidation ? 'translate-x-5' : 'translate-x-0.5'
                                                        }`}
                                                    />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Auto-Approve Section (hypotheses + plans) */}
                                        <div className="px-3 py-2">
                                            <div className="flex items-center justify-between">
                                                <div className="flex-1">
                                                    <label className="text-xs font-medium text-fg-secondary block">Auto-Approve</label>
                                                    <p className="text-xs text-fg-tertiary mt-0.5">Auto-approve hypotheses & plans after 20s</p>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => setAutoAcceptPlans(!autoAcceptPlans)}
                                                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ml-3 ${
                                                        autoAcceptPlans ? 'bg-accent' : 'bg-border-default'
                                                    }`}
                                                >
                                                    <span
                                                        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-surface transition-transform duration-200 ${
                                                            autoAcceptPlans ? 'translate-x-5' : 'translate-x-0.5'
                                                        }`}
                                                    />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            </div>

                            {/* Right Side - Send/Stop Button */}
                            <div
                                data-testid="chat-composer-footer-right"
                                className="justify-self-end flex items-center"
                            >
                                {isLoading ? (
                                    <button
                                        type="button"
                                        onClick={onCancelWorkflow}
                                        className="p-1.5 rounded bg-danger text-white hover:bg-danger transition-colors"
                                        title="Stop"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                            <rect x="6" y="6" width="12" height="12" rx="1" />
                                        </svg>
                                    </button>
                                ) : (
                                    <button
                                        type="submit"
                                        disabled={!canSubmit}
                                        className="p-1.5 rounded bg-accent-copper text-white hover:opacity-90 disabled:bg-border-default disabled:cursor-not-allowed transition-all"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="22" y1="2" x2="11" y2="13"></line>
                                            <polygon points="22,2 15,22 11,13 2,9"></polygon>
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </form>
                )}
            </div>
            <FeedbackModal
                isOpen={!!feedbackTarget}
                initialSentiment={feedbackInitialSentiment}
                onClose={() => {
                    setFeedbackTarget(null);
                    setFeedbackInitialSentiment(null);
                }}
                onSubmit={handleFeedbackSubmit}
            />
        </div>
    );
};

export default GeminiChatArea;
